import './index.css';
import AssetIcon from '../../../../components/AssetIcon';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import {
  formatBalance,
  formatPercentage,
} from '../../../../helpers/NumberFormatter';

import { Link } from 'react-router-dom';
import { Subscription } from '../../../../types/subscription';
import { useDispatch } from 'react-redux';
import { setActiveSubscriptionId } from '../../../../store/slices/subscription';
import { SortingToggleOptions } from '../../types';
import { colors } from '../../../../colors';
import BotItemChart from './Chart';
import { ExpandableModalChildProps } from '../../../../components/ExpandableModal';

interface Props {
  subscription: Subscription;
  activeToggleOption: SortingToggleOptions;
  parentRef: React.RefObject<HTMLDivElement>;
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  delayIndex: number;
}

const BotItem: React.FC<Props> = (props: Props) => {
  const {
    activeToggleOption,
    subscription,
    isModalOpen,
    setIsModalOpen,
    parentRef,
    delayIndex,
  } = props;

  const { baseAsset } = subscription;

  return (
    <BotItemContent
      subscription={subscription}
      activeToggleOption={activeToggleOption}
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      delayIndex={delayIndex}
    />
  );
  // return (
  //   <ExpandableModal key={baseAsset} parentRef={parentRef} supportScrolling>
  //     <BotItemContent
  //       subscription={subscription}
  //       activeToggleOption={activeToggleOption}
  //       isModalOpen={isModalOpen}
  //       setIsModalOpen={setIsModalOpen}
  //     />
  //   </ExpandableModal>
  // );
};

const BotItemContent = forwardRef<
  HTMLDivElement,
  ExpandableModalChildProps & {
    subscription: Subscription;
    activeToggleOption: SortingToggleOptions;
    isModalOpen: boolean;
    setIsModalOpen: (value: boolean) => void;
    delayIndex: number;
  }
>(
  (
    { subscription, activeToggleOption, setIsModalOpen, delayIndex, ...props },
    childRef,
  ) => {
    const dispatch = useDispatch();
    const { handleModalOpen, handleModalClose, style, isModalOpen } = props;

    const {
      isActive,
      baseAsset,
      quoteAsset,
      exchange,
      id,
      latestTransaction,
      totalBalanceInStable,
      assetBalance,
      stableBalance,
    } = subscription;

    const [isHovering, setIsHovering] = useState(false);
    const [shouldRenderChart, setShouldRenderChart] = useState(false);

    useEffect(() => {
      setShouldRenderChart(false);

      const id = setTimeout(() => {
        setShouldRenderChart(true);
      }, 1000 + 300 * delayIndex);

      return () => clearTimeout(id);
    }, [delayIndex, activeToggleOption]);

    const handleMouseEnter = useCallback(() => {
      setIsHovering(true);
    }, [setIsHovering]);

    const handleMouseLeave = useCallback(() => {
      setIsHovering(false);
    }, [setIsHovering]);

    const handleSubscriptionClick = () => {
      dispatch(setActiveSubscriptionId(id));
    };

    const handleClick = useCallback(() => {
      if (isModalOpen) {
        setIsModalOpen(false);
        handleModalClose?.();
        return;
      }

      // setIsModalOpen(true);
      // handleModalOpen?.();
    }, [handleModalClose, setIsModalOpen, isModalOpen]);

    const profitValue = useMemo(() => {
      switch (activeToggleOption) {
        case SortingToggleOptions.TotalValue:
        case SortingToggleOptions.Diversity:
          return subscription.profits?.total.stable;

        case SortingToggleOptions.TotalStableProfit:
          return subscription.profits?.total.stable;

        case SortingToggleOptions.TotalAssetProfit:
          return subscription.profits?.total.asset;

        case SortingToggleOptions.CurrentStableProfit:
          return subscription.profits?.current.stable;

        case SortingToggleOptions.CurrentAssetProfit:
          return subscription.profits?.current.asset;

        case SortingToggleOptions.NoAlgorithmicDifference:
          return subscription.profits?.total.stable;
      }
    }, [activeToggleOption, subscription.profits]);

    const profitColor =
      (profitValue?.relative || 0) < 0 ? colors.red : colors.green;

    const priceChangeLabel = useMemo(() => {
      if (activeToggleOption === SortingToggleOptions.NoAlgorithmicDifference) {
        return 'Difference';
      }

      return 'Price Change';
    }, [activeToggleOption]);

    const priceChangeValue = useMemo(() => {
      if (activeToggleOption === SortingToggleOptions.NoAlgorithmicDifference) {
        return subscription.profits?.noAlgorithmicProgressionProfit.relative;
      }

      if (
        activeToggleOption === SortingToggleOptions.TotalValue ||
        activeToggleOption === SortingToggleOptions.Diversity ||
        activeToggleOption === SortingToggleOptions.TotalStableProfit ||
        activeToggleOption === SortingToggleOptions.TotalAssetProfit
      ) {
        if (!!subscription.chartData) {
          return (
            (subscription.chartData?.dataPoints.noAlgorithmicProgression[
              subscription.chartData?.dataPoints.noAlgorithmicProgression
                .length - 1
            ].value -
              subscription.chartData?.dataPoints.noAlgorithmicProgression[0]
                .value) /
            subscription.chartData?.dataPoints.noAlgorithmicProgression[0].value
          );
        } else {
          return subscription.profits?.noAlgorithmicProgressionProfit.relative;
        }
      }

      return latestTransaction?.priceChange || 0;
    }, [
      activeToggleOption,
      latestTransaction?.priceChange,
      subscription.chartData,
      subscription.profits?.noAlgorithmicProgressionProfit,
    ]);

    const priceChangeColor = useMemo(() => {
      if (activeToggleOption === SortingToggleOptions.NoAlgorithmicDifference) {
        return (priceChangeValue || 0) < 0 ? colors.red : colors.green;
      }

      if (
        activeToggleOption === SortingToggleOptions.TotalValue ||
        activeToggleOption === SortingToggleOptions.Diversity ||
        activeToggleOption === SortingToggleOptions.TotalStableProfit
      ) {
        return (priceChangeValue || 0) < (profitValue?.relative || 0)
          ? colors.green
          : colors.red;
      }

      if (activeToggleOption === SortingToggleOptions.TotalAssetProfit) {
        return colors.white;
      }

      return profitColor;
    }, [activeToggleOption, priceChangeValue, profitColor, profitValue]);

    const profitLabel = useMemo(() => {
      switch (activeToggleOption) {
        case SortingToggleOptions.TotalValue:
        case SortingToggleOptions.Diversity:
          return 'Stable Profit';

        case SortingToggleOptions.TotalStableProfit:
          return 'Stable Profit';

        case SortingToggleOptions.TotalAssetProfit:
          return 'Asset Profit';

        case SortingToggleOptions.CurrentStableProfit:
          return 'Stable Profit';

        case SortingToggleOptions.CurrentAssetProfit:
          return 'Asset Profit';

        case SortingToggleOptions.NoAlgorithmicDifference:
          return 'Stable Profit';
      }
    }, [activeToggleOption]);

    return (
      <div
        ref={childRef}
        style={{
          border: '1px solid rgba(50, 50, 50, 1)',
          borderRadius: 8,
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'rgba(30, 30, 30, 1)',
          boxShadow: '4px 0px 5px rgba(255, 255, 255, 0.2)',
          flex: 1,
          minWidth: 'fit-content',
          opacity: isModalOpen ? 0 : 1,
          transition: 'opacity 0.5s ease',
          ...style,
        }}
        onClick={handleClick}
      >
        {/** First row, info and chart */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            columnGap: 8,
          }}
        >
          {/** Container with icon and pair */}
          <div style={{}}>
            {/** Asset icon */}
            <Link
              to={'/sub'}
              onClick={handleSubscriptionClick}
              style={{
                textDecoration: 'none',
                width: 'fit-content',
                display: 'block',
                marginBottom: 10,
              }}
            >
              <div style={{ position: 'relative' }}>
                {isHovering && (
                  <span
                    style={{
                      position: 'absolute',
                      color: colors.white,
                      transition: 'opacity 0.2s ease-in-out',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    {baseAsset}
                  </span>
                )}
                <AssetIcon
                  baseAsset={baseAsset}
                  isHovering={isHovering}
                  handleMouseEnter={handleMouseEnter}
                  handleMouseLeave={handleMouseLeave}
                  width={50}
                  height={50}
                />
                <div
                  style={{
                    position: 'absolute',
                    width: 10,
                    height: 10,
                    borderRadius: 10,
                    backgroundColor: isActive ? colors.green : colors.red,
                    borderWidth: 1.5,
                    borderStyle: 'solid',
                    borderColor: colors.white,
                    top: 0,
                    right: 0,
                  }}
                ></div>
              </div>
            </Link>
            {/** Pair */}
            <div style={{ color: colors.white }}>
              {/** Label */}
              <p
                style={{
                  margin: 0,
                  marginTop: 4,
                  marginBottom: -4,
                  color: colors.gray,
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Pair
              </p>
              {/** Value */}
              <p style={{ margin: 0, fontSize: 16 }}>
                {baseAsset}{' '}
                <span
                  style={{ color: colors.gray, fontSize: 12, fontWeight: 700 }}
                >
                  / {quoteAsset}
                </span>
              </p>
            </div>
            {/** Exchange */}
            <div style={{ color: colors.white, marginTop: -4 }}>
              {/** Label */}
              <p
                style={{
                  margin: 0,
                  marginTop: 4,
                  marginBottom: -4,
                  color: colors.gray,
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Exchange
              </p>
              {/** Value */}
              <p
                style={{ margin: 0, textTransform: 'capitalize', fontSize: 18 }}
              >
                {exchange.toLocaleLowerCase()}
              </p>
            </div>
            {/** Total Value */}
            <div style={{ color: colors.white, marginTop: -2 }}>
              {/** Label */}
              <p
                style={{
                  margin: 0,
                  marginTop: 4,
                  marginBottom: -4,
                  color: colors.gray,
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                Total Value
              </p>
              {/** Value */}
              <p
                style={{ margin: 0, textTransform: 'capitalize', fontSize: 18 }}
              >
                {formatBalance(totalBalanceInStable, true)}
              </p>
            </div>
          </div>
          {/** Container with chart and profits */}
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'end',
            }}
          >
            {/** Chart container */}
            <div
              style={{
                flex: 1,
                width: '100%',
              }}
            >
              {shouldRenderChart && (
                <BotItemChart
                  subscription={subscription}
                  activeToggleOption={activeToggleOption}
                />
              )}
            </div>
            {/** Profit container */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: 'row',
                flex: 1,
                columnGap: 16,
                flexGrow: 1,
                marginTop: 6,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/** Current profit */}
                <div style={{ color: colors.white }}>
                  {/** Label */}
                  <p
                    style={{
                      margin: 0,
                      marginBottom: -4,
                      color: colors.gray,
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    {profitLabel}
                  </p>
                  {/** Value */}
                  <p
                    style={{
                      margin: 0,
                      fontSize: 18,
                      color: profitColor,
                      fontWeight: 800,
                    }}
                  >
                    {formatPercentage(profitValue?.relative || 0, 2)}
                  </p>
                </div>
                {/** Stable balance */}
                <div style={{ color: colors.white }}>
                  {/** Label */}
                  <p
                    style={{
                      margin: 0,
                      marginBottom: -4,
                      color: colors.gray,
                      fontWeight: 600,
                      fontSize: 14,
                      marginTop: 2,
                    }}
                  >
                    Stable Balance
                  </p>
                  {/** Value */}
                  <p style={{ margin: 0, fontSize: 16 }}>
                    {stableBalance} {quoteAsset}
                  </p>
                </div>
              </div>
              {/** Balance container */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/** Current price change */}
                <div style={{ color: colors.white }}>
                  {/** Label */}
                  <p
                    style={{
                      margin: 0,
                      marginBottom: -4,
                      color: colors.gray,
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    {priceChangeLabel}
                  </p>
                  {/** Value */}
                  <p
                    style={{ margin: 0, fontSize: 18, color: priceChangeColor }}
                  >
                    {formatPercentage(priceChangeValue || 0)}
                  </p>
                </div>
                {/** Asset Balance */}
                <div style={{ color: colors.white }}>
                  {/** Label */}
                  <p
                    style={{
                      margin: 0,
                      marginBottom: -4,
                      color: colors.gray,
                      fontWeight: 600,
                      fontSize: 14,
                      marginTop: 2,
                    }}
                  >
                    Asset Balance
                  </p>
                  {/** Value */}
                  <p style={{ margin: 0, fontSize: 16 }}>
                    {assetBalance} {baseAsset}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

BotItemContent.displayName = 'BotItemContent';
export default BotItem;
